import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface Authentication {
  Id: string;
  Integrations: Array<string>;
  Description: string;
  CategoryId: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private api: AxiosInstance | null = null;
  private platformId = inject(PLATFORM_ID);
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.api = axios.create({
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Add request interceptor to dynamically set baseURL and include OAuth token
      this.api.interceptors.request.use((config) => {
        // Dynamically construct baseURL on each request to get current tenantId
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;

        const token = this.authService.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    }
  }

  async getAuthentications(): Promise<Array<Authentication> | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get('/authentication');
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async getAuthentication(id: string): Promise<Authentication | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get(`/authentication/${id}`);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async updateAuthentication(authentication: Authentication): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.put(`/authentication/${authentication.Id}`, authentication);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async createAuthentication(authentication: Authentication): Promise<any | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.post('/authentication', authentication);
      return response.data;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  async deleteAuthentication(id: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.delete(`/authentication/${id}`);
      return response;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }
}
